/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import React, { FC, useState, useEffect, useCallback } from "react";
import { ToastService } from "../../../toasts/ToastService";
import { ToastItem } from "./ToastItem";

export interface IToastProviderProps {
	toastService: ToastService;
}

export const ToastProvider: FC<IToastProviderProps> = ({ toastService }) => {
	const [ entries, setEntries ] = useState(() => toastService.getEntries().slice());

	useEffect(() => {
		const handleChange = () => setEntries(toastService.getEntries().slice());
		onEvent(toastService.onEntriesChange, handleChange);

		return () => {
			offEvent(toastService.onEntriesChange, handleChange);
		};
	}, [ toastService ]);

	const handleEntryClose = useCallback(
		(toastId: string) => {
			toastService.remove(toastId);
		},
		[ toastService ]
	);

	const handleEntryMouseEnter = useCallback(
		(toastId: string) => {
			toastService.disableTimeout(toastId);
		},
		[ toastService ]
	);

	const handleEntryMouseLeave = useCallback(
		(toastId: string) => {
			toastService.resetTimeout(toastId);
		},
		[ toastService ]
	);

	const baseClass = BEM("edt-toasts", "provider", {
		"is-empty": entries.length === 0
	});

	return (
		<div className={baseClass.toClassName()}>
			{entries.map((entry) => (
				<ToastItem
					key={entry.id}
					entry={entry}
					onClose={handleEntryClose}
					onMouseEnter={handleEntryMouseEnter}
					onMouseLeave={handleEntryMouseLeave}
				/>
			))}
		</div>
	);
};
